import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { hasAdminAccess, hasSuperAdminAccess } from '@/entities/user'
import { useAuth } from '@/features/auth'
import { ApiError, queryClient } from '@/shared/api'
import { routes } from '@/shared/config'
import { VideoCard } from '@/entities/video'
import type { VideoCardProps } from '@/entities/video'
import { VideoShelf } from '@/widgets/video-shelf'
import type { VideoShelfItem } from '@/widgets/video-shelf'
import { VideoSubscriptionUpsell } from '@/widgets/video-subscription-promo'
import { VideosShell } from '@/widgets/videos-shell'
import { typographyStyle } from '@/shared/lib/catalog'
import { designTokens } from '@/shared/lib/design-tokens'
import {
  applyBookmarkToCatalogCache,
  captureCatalogCacheSnapshots,
  isCatalogListQueryKey,
  restoreCatalogCacheSnapshots,
} from '../model/catalog-bookmarks-cache'
import {
  fetchCatalog,
  fetchCatalogBundleItems,
  fetchCatalogCollectionDetail,
  fetchCatalogCategoryItems,
  fetchCatalogVideoDetail,
  setCatalogFavorite,
  type CatalogListItem,
  type CatalogSearchItem,
  type CatalogResponse,
} from '../api/catalog-api'
import { CatalogBackButton } from './catalog-back-button'

type BrowseRowState = {
  row: {
    rowKind: 'category' | 'bundle'
    id: string
    title: string
    urlSlug: string | null
    position: number | null
    createdAt: string | null
  }
  items: CatalogListItem[]
  page: {
    nextCursor: string | null
  }
  isLoading: boolean
}

const BROWSE_CATEGORY_ITEMS_LIMIT = 12
const BROWSE_CATEGORIES_LIMIT = 8
const SEARCH_RESULTS_LIMIT = 24
const SCROLL_THRESHOLD_PX = 320
const CATALOG_QUERY_STALE_TIME_MS = 30_000
const VIEWER_PREFETCH_STALE_TIME_MS = 30_000
const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'in_trial', 'pending_cancellation'])
const INACTIVE_SUBSCRIPTION_STATUSES = new Set(['former', 'never_subscribed', 'on_hold'])

const catalogSectionTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.onboarding.titleDesktop),
}

const catalogSectionViewAllStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: 'rgba(254, 254, 254, 0.7)',
  ...typographyStyle(designTokens.typography.body.base),
}

const responsiveCatalogCardGridClass =
  'grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4'

const itemId = (item: CatalogListItem | CatalogSearchItem) => `${item.kind}:${item.id}`

const toSearchItem = (item: CatalogListItem | CatalogSearchItem): CatalogSearchItem => ({
  kind: item.kind,
  id: item.id,
  title: item.title,
  urlSlug: item.urlSlug,
  coverUrl: item.coverUrl,
  shortDescription: item.shortDescription,
  totalVideos: item.totalVideos,
  totalDurationSeconds: item.totalDurationSeconds,
  durationSeconds: item.durationSeconds,
  fileSizeBytes: item.fileSizeBytes,
  isBookmarked: item.isBookmarked,
  access: item.access,
  openUrl: item.openUrl,
  categories: 'categories' in item ? item.categories : [],
})

const formatDurationLabel = (seconds: number | null): string | undefined => {
  if (!seconds || seconds <= 0) {
    return undefined
  }

  const total = Math.floor(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = total % 60

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  return `${minutes}:${String(secs).padStart(2, '0')}`
}

const formatCollectionVideosLabel = (count: number | null): string | undefined => {
  if (count === null || !Number.isFinite(count)) {
    return undefined
  }

  const normalized = Math.max(0, Math.floor(count))
  const remainder10 = normalized % 10
  const remainder100 = normalized % 100

  if (remainder10 === 1 && remainder100 !== 11) {
    return `${normalized} видео`
  }

  if (remainder10 >= 2 && remainder10 <= 4 && (remainder100 < 12 || remainder100 > 14)) {
    return `${normalized} видео`
  }

  return `${normalized} видео`
}

const formatCollectionDurationMeta = (seconds: number | null): string | undefined => {
  if (!seconds || seconds <= 0) {
    return undefined
  }

  const total = Math.floor(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)

  if (hours > 0 && minutes > 0) {
    return `${hours} ч ${minutes} мин`
  }

  if (hours > 0) {
    return `${hours} ч`
  }

  return `${minutes} мин`
}

const catalogPlaceholderCover = '/no-image.png'

const withDetailRouteContext = (path: string, search: string) => {
  if (!search) {
    return path
  }

  return `${path}${search.startsWith('?') ? search : `?${search}`}`
}

const toVideoCardProps = (
  item: CatalogListItem | CatalogSearchItem,
  navigate: ReturnType<typeof useNavigate>,
  detailRouteSearch: string,
  isBookmarkPending = false,
  onBookmarkToggle?: () => void,
  onOpenStart?: (item: CatalogListItem | CatalogSearchItem) => void
): VideoCardProps => {
  const isLocked = item.access.state === 'locked'
  const openUrl = !isLocked
    ? item.kind === 'collection'
      ? `/catalog/collections/${item.urlSlug}`
      : item.openUrl
    : null

  return {
    coverSrc: item.coverUrl ?? catalogPlaceholderCover,
    title: item.title,
    shortDescription: item.shortDescription,
    contentTypeLabel: item.kind === 'collection' ? 'Коллекция' : 'Видео',
    contentCountLabel:
      item.kind === 'collection' ? formatCollectionVideosLabel(item.totalVideos) : undefined,
    durationMetaLabel:
      item.kind === 'collection'
        ? formatCollectionDurationMeta(item.totalDurationSeconds)
        : undefined,
    accessState: item.access.state,
    durationLabel: item.kind === 'video' ? formatDurationLabel(item.durationSeconds) : undefined,
    isBookmarked: item.isBookmarked,
    isBookmarkPending,
    onBookmarkToggle,
    onClick: openUrl
      ? () => {
          onOpenStart?.(item)
          navigate(withDetailRouteContext(openUrl, detailRouteSearch))
        }
      : undefined,
    onHoverStart: openUrl
      ? () => {
          onOpenStart?.(item)
        }
      : undefined,
  }
}

const toShelfItem = (
  item: CatalogListItem,
  navigate: ReturnType<typeof useNavigate>,
  detailRouteSearch: string,
  isBookmarkPending = false,
  onBookmarkToggle?: () => void,
  onOpenStart?: (item: CatalogListItem | CatalogSearchItem) => void
): VideoShelfItem => ({
  id: itemId(item),
  ...toVideoCardProps(
    item,
    navigate,
    detailRouteSearch,
    isBookmarkPending,
    onBookmarkToggle,
    onOpenStart
  ),
})

const toShelfTitle = (input: {
  mode: 'browse' | 'search'
  categoryTitle?: string
  searchValue: string | null
  favoritesOnly?: boolean
  historyOnly?: boolean
}) => {
  if (input.mode === 'browse') {
    return input.categoryTitle ?? 'Категория'
  }

  const categoryPrefix = input.categoryTitle?.trim()
  let baseTitle: string

  if (input.historyOnly) {
    baseTitle = 'Вы смотрели ранее'
  } else if (input.favoritesOnly) {
    baseTitle = input.searchValue?.trim() ? `Избранное: ${input.searchValue}` : 'Избранное'
  } else if (input.searchValue?.trim()) {
    baseTitle = `Результаты поиска: ${input.searchValue}`
  } else {
    baseTitle = 'Результаты поиска'
  }

  return categoryPrefix ? `${categoryPrefix}. ${baseTitle}` : baseTitle
}

const uniqueNonEmptyTerms = (terms: Array<string | null | undefined>) => {
  const seen = new Set<string>()
  const result: string[] = []

  for (const term of terms) {
    const trimmed = term?.trim()
    if (!trimmed || seen.has(trimmed)) {
      continue
    }

    seen.add(trimmed)
    result.push(trimmed)
  }

  return result
}

const buildCatalogSearchTitle = (input: {
  query: CatalogResponse['query']
  categoryTitle?: string
  bundles: CatalogResponse['bundles']
  filters: CatalogResponse['filters']
}) => {
  if (input.query.history) {
    return toShelfTitle({
      mode: 'search',
      categoryTitle: input.categoryTitle,
      searchValue: input.query.search,
      historyOnly: true,
    })
  }

  if (input.query.favorites) {
    return toShelfTitle({
      mode: 'search',
      categoryTitle: input.categoryTitle,
      searchValue: input.query.search,
      favoritesOnly: true,
    })
  }

  const selectedBundleTitle = input.query.bundle
    ? input.bundles.find((bundle) => bundle.id === input.query.bundle)?.title
    : null
  const selectedFilterTitles = input.filters.flatMap((filter) => {
    if (isCategoryFilterSection(filter.title, filter.queryKey)) {
      return []
    }

    if (isKeywordFilterSection(filter.title, filter.queryKey)) {
      return []
    }

    const selectedOptionIds = new Set(filter.selectedOptionIds)
    return filter.options
      .filter((option) => selectedOptionIds.has(option.id))
      .map((option) => option.title)
  })
  const searchTerms = uniqueNonEmptyTerms([
    selectedBundleTitle,
    ...selectedFilterTitles,
    input.query.search,
  ])
  const categoryTitle = input.categoryTitle?.trim()

  if (searchTerms.length === 0) {
    return categoryTitle || 'Результаты поиска'
  }

  const searchTitle = `Результаты поиска по "${searchTerms.join(', ')}"`
  return categoryTitle ? `${categoryTitle}. ${searchTitle}` : searchTitle
}

const summarizeApiError = (error: unknown) => {
  if (error instanceof ApiError) {
    return `Ошибка загрузки каталога (${error.status})`
  }

  return 'Не удалось загрузить каталог'
}

const isActiveQuery = (params: URLSearchParams) => {
  for (const key of params.keys()) {
    if (
      key === 'search' ||
      key === 'bundle' ||
      key === 'category' ||
      key === 'favorites' ||
      key.startsWith('filter.') ||
      key.startsWith('catalog_filter_')
    ) {
      return true
    }
  }

  return false
}

const isFavoritesMode = (params: URLSearchParams): boolean => {
  const raw = params.get('favorites')
  if (!raw) {
    return false
  }

  const normalized = raw.trim().toLowerCase()
  return normalized === '1' || normalized === 'true' || normalized === 'yes'
}

const isHistoryMode = (params: URLSearchParams): boolean => {
  const raw = params.get('history')
  if (!raw) {
    return false
  }

  const normalized = raw.trim().toLowerCase()
  return normalized === '1' || normalized === 'true' || normalized === 'yes'
}

const clearPaginationParams = (params: URLSearchParams) => {
  params.delete('searchCursor')
  params.delete('browseCategoriesCursor')
}

const clearFilterParams = (params: URLSearchParams) => {
  const keysToDelete = [...params.keys()].filter(
    (key) =>
      key === 'category' ||
      key === 'bundle' ||
      key.startsWith('filter.') ||
      key.startsWith('catalog_filter_')
  )

  for (const key of keysToDelete) {
    params.delete(key)
  }
}

const buildFavoritesOverviewParams = () => {
  const params = new URLSearchParams()
  params.set('favorites', '1')
  return params
}

const buildCatalogHomeParams = () => new URLSearchParams()

const getBrowseSource = (params: URLSearchParams): 'categories' | 'bundles' =>
  params.get('source') === 'bundles' ? 'bundles' : 'categories'

const isCategoryFilterSection = (title: string, queryKey: string) => {
  if (queryKey.trim().toLowerCase() === 'category') {
    return true
  }

  const normalizedTitle = title.trim().toLowerCase()
  return normalizedTitle === 'категория' || normalizedTitle === 'category'
}

const isKeywordFilterSection = (title: string, queryKey: string) => {
  const normalizedTitle = title.trim().toLowerCase()
  const normalizedQueryKey = queryKey.trim().toLowerCase()

  return (
    normalizedTitle === 'ключевые слова' ||
    normalizedTitle === 'keywords' ||
    normalizedQueryKey === 'search' ||
    normalizedQueryKey === 'keywords' ||
    normalizedQueryKey === 'keyword'
  )
}

export function VideosPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { logout, user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const searchParamsString = searchParams.toString()
  const browseSource = getBrowseSource(searchParams)
  const favoritesOnly = isFavoritesMode(searchParams)
  const historyOnly = isHistoryMode(searchParams)
  const currentSearch = searchParams.get('search') ?? ''
  const [searchInput, setSearchInput] = useState(currentSearch)
  const [browseRowsById, setBrowseRowsById] = useState<Record<string, BrowseRowState>>({})
  const [browseRowOrder, setBrowseRowOrder] = useState<string[]>([])
  const [browseCategoriesNextCursor, setBrowseCategoriesNextCursor] = useState<string | null>(null)
  const [isBrowseCategoriesLoading, setIsBrowseCategoriesLoading] = useState(false)
  const [categoryItemsError, setCategoryItemsError] = useState<string | null>(null)
  const [searchItems, setSearchItems] = useState<CatalogSearchItem[]>([])
  const detailRouteSearch = useMemo(() => {
    const nextParams = new URLSearchParams()

    if (favoritesOnly) {
      nextParams.set('favorites', '1')
    }

    if (browseSource === 'bundles') {
      nextParams.set('source', 'bundles')
      const selectedBundleId = searchParams.get('bundle')
      if (selectedBundleId) {
        nextParams.set('bundle', selectedBundleId)
      }
    }

    const serialized = nextParams.toString()
    return serialized ? `?${serialized}` : ''
  }, [browseSource, favoritesOnly, searchParams])

  useEffect(() => {
    document.title = 'Каталог | Epic Growth'
  }, [])
  const [searchNextCursor, setSearchNextCursor] = useState<string | null>(null)
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const [searchItemsError, setSearchItemsError] = useState<string | null>(null)
  const [bookmarkPendingIds, setBookmarkPendingIds] = useState<Set<string>>(new Set())
  const [pendingNavigationItem, setPendingNavigationItem] = useState<'home' | 'bookmarks' | null>(
    null
  )
  const [pendingBrowseRouteId, setPendingBrowseRouteId] = useState<string | null>(null)
  const mainScrollRef = useRef<HTMLElement | null>(null)
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null)
  const browseRowsRef = useRef<Record<string, BrowseRowState>>({})
  const browseCategoriesCursorRef = useRef<string | null>(null)
  const searchCursorRef = useRef<string | null>(null)
  const searchItemsRef = useRef<CatalogSearchItem[]>([])
  const bookmarkPendingIdsRef = useRef<Set<string>>(new Set())
  const browseCategoriesLoadingRef = useRef(false)
  const searchLoadingRef = useRef(false)
  const categoryLoadingRef = useRef<Set<string>>(new Set())
  const browseCategoriesInFlightCursorRef = useRef<string | null>(null)
  const searchInFlightCursorRef = useRef<string | null>(null)
  const prefetchedViewerKeysRef = useRef<Set<string>>(new Set())

  const catalogQuery = useQuery({
    queryKey: ['catalog', searchParamsString],
    queryFn: () => fetchCatalog(new URLSearchParams(searchParamsString)),
    placeholderData: (previousData) => previousData,
    staleTime: CATALOG_QUERY_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })
  const favoritesOverview = catalogQuery.data?.favoritesOverview ?? null
  const isFavoritesOverviewMode =
    favoritesOnly && catalogQuery.data?.mode === 'search' && Boolean(favoritesOverview)

  const prefetchViewerForItem = useCallback((item: CatalogListItem | CatalogSearchItem) => {
    if (item.access.state === 'locked') {
      return
    }

    const prefetchKey = `${item.kind}:${item.urlSlug}`
    if (prefetchedViewerKeysRef.current.has(prefetchKey)) {
      return
    }

    prefetchedViewerKeysRef.current.add(prefetchKey)

    if (item.kind === 'video') {
      void queryClient.prefetchQuery({
        queryKey: ['catalog', 'video', item.urlSlug],
        staleTime: VIEWER_PREFETCH_STALE_TIME_MS,
        queryFn: () => fetchCatalogVideoDetail(item.urlSlug),
      })
      return
    }

    void queryClient.prefetchQuery({
      queryKey: ['catalog', 'collection', item.urlSlug, null],
      staleTime: VIEWER_PREFETCH_STALE_TIME_MS,
      queryFn: () =>
        fetchCatalogCollectionDetail({
          collectionUrl: item.urlSlug,
          selectedVideoSlug: null,
        }),
    })
  }, [])

  const buildFavoritesPreviewItems = useCallback(() => {
    const favoritesById = new Map<string, CatalogSearchItem>()

    for (const item of searchItemsRef.current) {
      if (!item.isBookmarked) {
        continue
      }
      favoritesById.set(itemId(item), toSearchItem(item))
    }

    for (const row of Object.values(browseRowsRef.current)) {
      for (const item of row.items) {
        if (!item.isBookmarked) {
          continue
        }
        favoritesById.set(itemId(item), toSearchItem(item))
      }
    }

    return [...favoritesById.values()]
  }, [])

  useEffect(() => {
    setSearchInput(currentSearch)
  }, [currentSearch])

  useEffect(() => {
    browseRowsRef.current = browseRowsById
  }, [browseRowsById])

  useEffect(() => {
    browseCategoriesCursorRef.current = browseCategoriesNextCursor
  }, [browseCategoriesNextCursor])

  useEffect(() => {
    searchCursorRef.current = searchNextCursor
  }, [searchNextCursor])

  useEffect(() => {
    searchItemsRef.current = searchItems
  }, [searchItems])

  useEffect(() => {
    bookmarkPendingIdsRef.current = bookmarkPendingIds
  }, [bookmarkPendingIds])

  useEffect(() => {
    if (!pendingNavigationItem) {
      return
    }

    const targetFavorites = pendingNavigationItem === 'bookmarks'
    if (targetFavorites === favoritesOnly && !catalogQuery.isFetching) {
      setPendingNavigationItem(null)
    }
  }, [catalogQuery.isFetching, favoritesOnly, pendingNavigationItem])

  useEffect(() => {
    if (!catalogQuery.isFetching) {
      setPendingBrowseRouteId(null)
    }
  }, [catalogQuery.isFetching])

  useEffect(() => {
    const trimmed = searchInput.trim()
    const normalizedCurrent = currentSearch.trim()

    if (trimmed === normalizedCurrent) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      const nextParams = new URLSearchParams(searchParamsString)

      if (trimmed.length === 0) {
        nextParams.delete('search')
      } else {
        nextParams.set('search', trimmed)
      }

      if (!isActiveQuery(nextParams)) {
        nextParams.delete('searchCursor')
        nextParams.delete('browseCategoriesCursor')
      }

      setSearchParams(nextParams, { replace: true, preventScrollReset: true })
    }, 350)

    return () => window.clearTimeout(timeoutId)
  }, [currentSearch, searchInput, searchParamsString, setSearchParams])

  useEffect(() => {
    const data = catalogQuery.data
    if (!data) {
      return
    }

    if (data.mode === 'browse' && data.browse) {
      const nextRows: Record<string, BrowseRowState> = {}
      for (const row of data.browse.rows) {
        nextRows[row.id] = {
          row: {
            rowKind: row.rowKind,
            id: row.id,
            title: row.title,
            urlSlug: row.urlSlug,
            position: row.position,
            createdAt: row.createdAt,
          },
          items: row.items,
          page: {
            nextCursor: row.itemsPage.nextCursor,
          },
          isLoading: false,
        }
      }

      setBrowseRowsById(nextRows)
      setBrowseRowOrder(data.browse.rows.map((row) => row.id))
      setBrowseCategoriesNextCursor(data.browse.page.nextCursor)
      setIsBrowseCategoriesLoading(false)
      setCategoryItemsError(null)
      setSearchItems([])
      setSearchNextCursor(null)
      setIsSearchLoading(false)
      setSearchItemsError(null)
      searchLoadingRef.current = false
      searchCursorRef.current = null
      searchInFlightCursorRef.current = null
      browseCategoriesLoadingRef.current = false
      browseCategoriesInFlightCursorRef.current = null
      return
    }

    if (data.mode === 'search' && data.searchResults) {
      setSearchItems(data.searchResults.items)
      setSearchNextCursor(data.searchResults.nextCursor)
      setIsSearchLoading(false)
      setSearchItemsError(null)
      setBrowseRowsById({})
      setBrowseRowOrder([])
      setBrowseCategoriesNextCursor(null)
      setIsBrowseCategoriesLoading(false)
      setCategoryItemsError(null)
      browseCategoriesLoadingRef.current = false
      browseCategoriesCursorRef.current = null
      browseCategoriesInFlightCursorRef.current = null
      searchLoadingRef.current = false
      searchInFlightCursorRef.current = null
    }
  }, [catalogQuery.data])

  const getRemainingScrollablePixels = useCallback(() => {
    const scrollHost = mainScrollRef.current
    if (scrollHost) {
      return scrollHost.scrollHeight - scrollHost.scrollTop - scrollHost.clientHeight
    }

    const doc = document.documentElement
    return doc.scrollHeight - window.scrollY - window.innerHeight
  }, [])

  const isNearScrollEnd = useCallback(
    () => getRemainingScrollablePixels() <= SCROLL_THRESHOLD_PX,
    [getRemainingScrollablePixels]
  )

  const loadMoreBrowseRowItems = useCallback(async (rowId: string) => {
    if (categoryLoadingRef.current.has(rowId)) {
      return
    }

    const rowState = browseRowsRef.current[rowId]
    if (!rowState?.page.nextCursor || rowState.isLoading) {
      return
    }

    categoryLoadingRef.current.add(rowId)
    setCategoryItemsError(null)
    setBrowseRowsById((current) => ({
      ...(current[rowId]
        ? {
            ...current,
            [rowId]: {
              ...current[rowId],
              isLoading: true,
            },
          }
        : current),
    }))

    try {
      const response =
        rowState.row.rowKind === 'bundle'
          ? await fetchCatalogBundleItems({
              bundleId: rowId,
              cursor: rowState.page.nextCursor,
              limit: BROWSE_CATEGORY_ITEMS_LIMIT,
            })
          : await fetchCatalogCategoryItems({
              categoryId: rowId,
              cursor: rowState.page.nextCursor,
              limit: BROWSE_CATEGORY_ITEMS_LIMIT,
            })

      setBrowseRowsById((current) => {
        const existing = current[rowId]
        if (!existing) {
          return current
        }

        const seenIds = new Set(existing.items.map(itemId))
        const appendedItems = response.items.filter((item) => {
          const id = itemId(item)
          if (seenIds.has(id)) {
            return false
          }
          seenIds.add(id)
          return true
        })

        return {
          ...current,
          [rowId]: {
            ...existing,
            items: [...existing.items, ...appendedItems],
            page: {
              nextCursor:
                response.page.nextCursor === rowState.page.nextCursor
                  ? null
                  : response.page.nextCursor,
            },
            isLoading: false,
          },
        }
      })
    } catch (error) {
      setCategoryItemsError(summarizeApiError(error))
      setBrowseRowsById((current) => ({
        ...(current[rowId]
          ? {
              ...current,
              [rowId]: {
                ...current[rowId],
                isLoading: false,
              },
            }
          : current),
      }))
    } finally {
      categoryLoadingRef.current.delete(rowId)
    }
  }, [])

  const loadMoreBrowseCategories = useCallback(async () => {
    if (browseCategoriesLoadingRef.current) {
      return
    }

    const nextCursor = browseCategoriesCursorRef.current
    if (!nextCursor) {
      return
    }
    if (browseCategoriesInFlightCursorRef.current === nextCursor) {
      return
    }

    browseCategoriesLoadingRef.current = true
    browseCategoriesInFlightCursorRef.current = nextCursor
    setIsBrowseCategoriesLoading(true)
    setCategoryItemsError(null)
    let nextCursorAfterLoad: string | null = null

    try {
      const nextParams = new URLSearchParams(searchParamsString)
      nextParams.set('browseCategoriesCursor', nextCursor)
      nextParams.set('browseCategoriesLimit', String(BROWSE_CATEGORIES_LIMIT))
      nextParams.set('browseCategoryItemsLimit', String(BROWSE_CATEGORY_ITEMS_LIMIT))

      const response = await fetchCatalog(nextParams)
      if (response.mode !== 'browse' || !response.browse) {
        setIsBrowseCategoriesLoading(false)
        return
      }

      setBrowseRowsById((current) => {
        const next = { ...current }

        for (const row of response.browse?.rows ?? []) {
          const existing = next[row.id]
          if (!existing) {
            next[row.id] = {
              row: {
                rowKind: row.rowKind,
                id: row.id,
                title: row.title,
                urlSlug: row.urlSlug,
                position: row.position,
                createdAt: row.createdAt,
              },
              items: row.items,
              page: {
                nextCursor: row.itemsPage.nextCursor,
              },
              isLoading: false,
            }
            continue
          }

          const seenIds = new Set(existing.items.map(itemId))
          const mergedItems = [...existing.items]
          for (const item of row.items) {
            const id = itemId(item)
            if (seenIds.has(id)) {
              continue
            }
            seenIds.add(id)
            mergedItems.push(item)
          }

          next[row.id] = {
            ...existing,
            row: {
              rowKind: row.rowKind,
              id: row.id,
              title: row.title,
              urlSlug: row.urlSlug,
              position: row.position,
              createdAt: row.createdAt,
            },
            items: mergedItems,
            page: {
              nextCursor: row.itemsPage.nextCursor,
            },
            isLoading: false,
          }
        }

        return next
      })

      setBrowseRowOrder((current) => {
        const seenIds = new Set(current)
        const appendedIds = response.browse?.rows
          .map((row) => row.id)
          .filter((rowId) => {
            if (seenIds.has(rowId)) {
              return false
            }
            seenIds.add(rowId)
            return true
          })

        return appendedIds ? [...current, ...appendedIds] : current
      })
      nextCursorAfterLoad =
        response.browse.page.nextCursor === nextCursor ? null : response.browse.page.nextCursor
      browseCategoriesCursorRef.current = nextCursorAfterLoad
      setBrowseCategoriesNextCursor(nextCursorAfterLoad)
    } catch (error) {
      setCategoryItemsError(summarizeApiError(error))
    } finally {
      browseCategoriesLoadingRef.current = false
      browseCategoriesInFlightCursorRef.current = null
      setIsBrowseCategoriesLoading(false)

      if (nextCursorAfterLoad && isNearScrollEnd()) {
        window.requestAnimationFrame(() => {
          void loadMoreBrowseCategories()
        })
      }
    }
  }, [isNearScrollEnd, searchParamsString])

  const loadMoreSearchResults = useCallback(async () => {
    if (searchLoadingRef.current) {
      return
    }

    const nextCursor = searchCursorRef.current
    if (!nextCursor) {
      return
    }
    if (searchInFlightCursorRef.current === nextCursor) {
      return
    }

    searchLoadingRef.current = true
    searchInFlightCursorRef.current = nextCursor
    setIsSearchLoading(true)
    setSearchItemsError(null)
    let nextCursorAfterLoad: string | null = null

    try {
      const nextParams = new URLSearchParams(searchParamsString)
      nextParams.set('searchCursor', nextCursor)
      nextParams.set('searchLimit', String(SEARCH_RESULTS_LIMIT))

      const response = await fetchCatalog(nextParams)
      if (response.mode !== 'search' || !response.searchResults) {
        setIsSearchLoading(false)
        return
      }

      setSearchItems((current) => {
        const seenIds = new Set(current.map(itemId))
        const mergedItems = [...current]

        for (const item of response.searchResults?.items ?? []) {
          const id = itemId(item)
          if (seenIds.has(id)) {
            continue
          }
          seenIds.add(id)
          mergedItems.push(item)
        }

        return mergedItems
      })
      nextCursorAfterLoad =
        response.searchResults.nextCursor === nextCursor ? null : response.searchResults.nextCursor
      searchCursorRef.current = nextCursorAfterLoad
      setSearchNextCursor(nextCursorAfterLoad)
    } catch (error) {
      setSearchItemsError(summarizeApiError(error))
    } finally {
      searchLoadingRef.current = false
      searchInFlightCursorRef.current = null
      setIsSearchLoading(false)

      if (nextCursorAfterLoad && isNearScrollEnd()) {
        window.requestAnimationFrame(() => {
          void loadMoreSearchResults()
        })
      }
    }
  }, [isNearScrollEnd, searchParamsString])

  const applyBookmarkState = useCallback(
    (input: { itemKind: 'collection' | 'video'; itemId: string; bookmarked: boolean }) => {
      const key = `${input.itemKind}:${input.itemId}`
      const shouldRemoveFromSearch = favoritesOnly && !input.bookmarked

      setBrowseRowsById((current) => {
        const next: Record<string, BrowseRowState> = {}
        for (const [rowId, row] of Object.entries(current)) {
          next[rowId] = {
            ...row,
            items: row.items.map((item) =>
              item.kind === input.itemKind && item.id === input.itemId
                ? { ...item, isBookmarked: input.bookmarked }
                : item
            ),
          }
        }
        return next
      })

      setSearchItems((current) => {
        const mapped = current.map((item) =>
          item.kind === input.itemKind && item.id === input.itemId
            ? { ...item, isBookmarked: input.bookmarked }
            : item
        )

        if (shouldRemoveFromSearch) {
          return mapped.filter((item) => itemId(item) !== key)
        }

        return mapped
      })
    },
    [favoritesOnly]
  )

  const handleBookmarkToggle = useCallback(
    async (item: CatalogListItem | CatalogSearchItem) => {
      const key = itemId(item)
      if (bookmarkPendingIdsRef.current.has(key)) {
        return
      }

      const nextBookmarked = !item.isBookmarked
      const previousBrowseRows = browseRowsRef.current
      const previousSearchItems = searchItemsRef.current
      const previousCatalogCache = captureCatalogCacheSnapshots()

      setBookmarkPendingIds((current) => new Set(current).add(key))
      applyBookmarkState({
        itemKind: item.kind,
        itemId: item.id,
        bookmarked: nextBookmarked,
      })
      applyBookmarkToCatalogCache({
        itemKind: item.kind,
        itemId: item.id,
        bookmarked: nextBookmarked,
        seedItem: {
          kind: item.kind,
          id: item.id,
          title: item.title,
          urlSlug: item.urlSlug,
          coverUrl: item.coverUrl,
          shortDescription: item.shortDescription,
          totalVideos: item.totalVideos,
          totalDurationSeconds: item.totalDurationSeconds,
          durationSeconds: item.durationSeconds,
          fileSizeBytes: item.fileSizeBytes,
          access: item.access,
          openUrl: item.openUrl,
          isBookmarked: nextBookmarked,
          categories: 'categories' in item ? item.categories : [],
        },
      })

      try {
        await setCatalogFavorite({
          itemKind: item.kind,
          itemId: item.id,
          bookmarked: nextBookmarked,
        })
      } catch {
        setBrowseRowsById(previousBrowseRows)
        setSearchItems(previousSearchItems)
        restoreCatalogCacheSnapshots(previousCatalogCache)
      } finally {
        setBookmarkPendingIds((current) => {
          const next = new Set(current)
          next.delete(key)
          return next
        })
        void queryClient.invalidateQueries({
          predicate: (query) => isCatalogListQueryKey(query.queryKey),
        })
      }
    },
    [applyBookmarkState]
  )

  const handleCategorySelect = useCallback(
    (categorySlug: string | null) => {
      const nextParams = new URLSearchParams(searchParamsString)

      if (categorySlug) {
        nextParams.set('category', categorySlug)
      } else {
        nextParams.delete('category')
      }

      clearPaginationParams(nextParams)
      setSearchParams(nextParams, { replace: true, preventScrollReset: true })
    },
    [searchParamsString, setSearchParams]
  )

  const handleBundleSelect = useCallback(
    (bundleId: string | null) => {
      const nextParams = new URLSearchParams(searchParamsString)

      if (bundleId) {
        nextParams.set('bundle', bundleId)
      } else {
        nextParams.delete('bundle')
      }

      clearPaginationParams(nextParams)
      setSearchParams(nextParams, { replace: true, preventScrollReset: true })
    },
    [searchParamsString, setSearchParams]
  )

  const handleFilterSelectionChange = useCallback(
    (selectedByFilterId: Record<string, string[]>) => {
      const nextParams = new URLSearchParams(searchParamsString)

      for (const key of [...nextParams.keys()]) {
        if (key.startsWith('filter.') || key.startsWith('catalog_filter_')) {
          nextParams.delete(key)
        }
      }

      for (const [filterId, selectedOptionIds] of Object.entries(selectedByFilterId)) {
        if (selectedOptionIds.length === 0) {
          continue
        }
        nextParams.set(`filter.${filterId}`, selectedOptionIds.join(','))
      }

      clearPaginationParams(nextParams)
      setSearchParams(nextParams, { replace: true, preventScrollReset: true })
    },
    [searchParamsString, setSearchParams]
  )

  const handleResetFilters = useCallback(() => {
    if (favoritesOnly) {
      setSearchParams(buildFavoritesOverviewParams(), { replace: true })
      return
    }

    const nextParams = new URLSearchParams(searchParamsString)
    const hasSearchQuery = Boolean((nextParams.get('search') ?? '').trim())

    if (hasSearchQuery) {
      setSearchParams(buildCatalogHomeParams(), { replace: true })
      return
    }

    clearFilterParams(nextParams)
    clearPaginationParams(nextParams)
    setSearchParams(nextParams, { replace: true })
  }, [favoritesOnly, searchParamsString, setSearchParams])

  const handleExitSearchMode = useCallback(() => {
    if (favoritesOnly) {
      setSearchParams(buildFavoritesOverviewParams(), { replace: true })
      return
    }

    setSearchParams(buildCatalogHomeParams(), { replace: true })
  }, [favoritesOnly, setSearchParams])

  const handleFavoritesOverviewBack = useCallback(() => {
    setSearchParams(buildFavoritesOverviewParams(), { replace: true })
  }, [setSearchParams])

  const handleNavigationSelect = useCallback(
    (itemId: 'home' | 'bookmarks' | 'subscription') => {
      if (itemId === 'subscription') {
        navigate(routes.catalogSubscription)
        return
      }

      const isExactHomeState =
        !favoritesOnly && !isActiveQuery(searchParams) && browseSource !== 'bundles'
      if (
        pendingNavigationItem ||
        (itemId === 'bookmarks' && favoritesOnly) ||
        (itemId === 'home' && isExactHomeState)
      ) {
        return
      }

      setPendingNavigationItem(itemId)
      if (itemId === 'bookmarks') {
        setSearchItems(buildFavoritesPreviewItems())
        setSearchItemsError(null)
        setSearchNextCursor(null)
      }

      if (itemId === 'bookmarks') {
        setSearchParams(buildFavoritesOverviewParams(), { replace: true })
      } else {
        const nextParams = new URLSearchParams(searchParamsString)
        nextParams.delete('favorites')
        nextParams.delete('history')
        nextParams.delete('search')
        clearFilterParams(nextParams)
        nextParams.delete('bundle')
        nextParams.delete('category')
        nextParams.delete('source')
        clearPaginationParams(nextParams)
        setSearchParams(nextParams, { replace: true })
      }
    },
    [
      buildFavoritesPreviewItems,
      browseSource,
      favoritesOnly,
      navigate,
      pendingNavigationItem,
      searchParams,
      searchParamsString,
      setSearchParams,
    ]
  )

  const handleCatalogScroll = useCallback(() => {
    if (!catalogQuery.data) {
      return
    }

    if (!isNearScrollEnd()) {
      return
    }

    if (catalogQuery.data.mode === 'browse') {
      void loadMoreBrowseCategories()
      return
    }

    if (catalogQuery.data.mode === 'search') {
      void loadMoreSearchResults()
    }
  }, [catalogQuery.data, isNearScrollEnd, loadMoreBrowseCategories, loadMoreSearchResults])

  useEffect(() => {
    const handleWindowScroll = () => {
      handleCatalogScroll()
    }

    window.addEventListener('scroll', handleWindowScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleWindowScroll)
    }
  }, [handleCatalogScroll])

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current

    if (!sentinel || !catalogQuery.data) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const isIntersecting = entries.some((entry) => entry.isIntersecting)
        if (!isIntersecting) {
          return
        }

        if (catalogQuery.data?.mode === 'browse') {
          void loadMoreBrowseCategories()
          return
        }

        if (catalogQuery.data?.mode === 'search') {
          void loadMoreSearchResults()
        }
      },
      {
        root: null,
        rootMargin: '1200px 0px 1200px 0px',
        threshold: 0.01,
      }
    )

    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [catalogQuery.data, loadMoreBrowseCategories, loadMoreSearchResults])

  useEffect(() => {
    if (!catalogQuery.data || catalogQuery.isPending) {
      return
    }

    if (!isNearScrollEnd()) {
      return
    }

    if (catalogQuery.data.mode === 'browse') {
      void loadMoreBrowseCategories()
      return
    }

    if (catalogQuery.data.mode === 'search') {
      void loadMoreSearchResults()
    }
  }, [
    browseRowOrder.length,
    browseCategoriesNextCursor,
    catalogQuery.data,
    catalogQuery.isPending,
    isNearScrollEnd,
    loadMoreBrowseCategories,
    loadMoreSearchResults,
    searchItems.length,
    searchNextCursor,
  ])

  const browseSections = useMemo(() => {
    return browseRowOrder
      .map((rowId) => {
        const rowState = browseRowsById[rowId]
        if (!rowState) {
          return null
        }

        return {
          id: rowState.row.id,
          rowKind: rowState.row.rowKind,
          urlSlug: rowState.row.urlSlug,
          title: toShelfTitle({
            mode: 'browse',
            categoryTitle: rowState.row.title,
            searchValue: null,
          }),
          items: rowState.items.map((item) =>
            toShelfItem(
              item,
              navigate,
              detailRouteSearch,
              bookmarkPendingIds.has(itemId(item)),
              () => {
                void handleBookmarkToggle(item)
              },
              prefetchViewerForItem
            )
          ),
          canLoadMore: Boolean(rowState.page.nextCursor),
          isLoading: rowState.isLoading,
        }
      })
      .filter((section): section is NonNullable<typeof section> => Boolean(section))
  }, [
    bookmarkPendingIds,
    browseRowOrder,
    browseRowsById,
    detailRouteSearch,
    handleBookmarkToggle,
    navigate,
    prefetchViewerForItem,
  ])

  const viewerIsInactive = useMemo(() => {
    const viewer = catalogQuery.data?.viewer
    if (!viewer) {
      return false
    }

    const normalizedStatus = viewer.subscriptionStatus?.trim().toLowerCase()
    if (normalizedStatus) {
      if (INACTIVE_SUBSCRIPTION_STATUSES.has(normalizedStatus)) {
        return true
      }

      if (ACTIVE_SUBSCRIPTION_STATUSES.has(normalizedStatus)) {
        return false
      }
    }

    return !viewer.subscriptionActive
  }, [catalogQuery.data?.viewer])

  const filterSections = useMemo(() => {
    if (!catalogQuery.data) {
      return []
    }

    return catalogQuery.data.filters
      .filter(
        (filter) =>
          !isCategoryFilterSection(filter.title, filter.queryKey) &&
          !isKeywordFilterSection(filter.title, filter.queryKey)
      )
      .map((filter) => ({
        id: filter.id,
        title: filter.title,
        options: filter.options.map((option) => ({
          id: option.id,
          title: option.title,
        })),
        selectedOptionIds: filter.selectedOptionIds,
      }))
  }, [catalogQuery.data])

  const categories = useMemo(() => {
    if (!catalogQuery.data) {
      return []
    }

    return catalogQuery.data.categories.map((category) => ({
      id: category.id,
      title: category.title,
      urlSlug: category.urlSlug,
    }))
  }, [catalogQuery.data])

  const bundles = useMemo(() => {
    if (!catalogQuery.data) {
      return []
    }

    return catalogQuery.data.bundles
  }, [catalogQuery.data])

  const selectedCategoryTitle = useMemo(() => {
    const selectedCategorySlug = catalogQuery.data?.query.category
    if (!selectedCategorySlug) {
      return undefined
    }

    return categories.find((category) => category.urlSlug === selectedCategorySlug)?.title
  }, [catalogQuery.data?.query.category, categories])

  const searchHeadingTitle = useMemo(() => {
    if (!catalogQuery.data || catalogQuery.data.mode !== 'search') {
      return null
    }

    return buildCatalogSearchTitle({
      query: catalogQuery.data.query,
      categoryTitle: selectedCategoryTitle,
      bundles,
      filters: catalogQuery.data.filters,
    })
  }, [bundles, catalogQuery.data, selectedCategoryTitle])

  const renderBrowseSection = (section: (typeof browseSections)[number]) => (
    <VideoShelf
      key={section.id}
      title={section.title}
      items={section.items}
      bleedRight
      showViewAll={Boolean(section.canLoadMore || section.rowKind === 'bundle' || section.urlSlug)}
      forceShowViewAll={section.canLoadMore}
      viewAllLabel={
        pendingBrowseRouteId === section.id
          ? 'Открываем...'
          : section.isLoading
            ? 'Загрузка...'
            : 'Показать ещё'
      }
      isViewAllDisabled={
        pendingBrowseRouteId === section.id || (section.canLoadMore ? section.isLoading : false)
      }
      onViewAllClick={() => {
        if (section.rowKind === 'bundle') {
          setPendingBrowseRouteId(section.id)
          navigate(`/catalog?bundle=${encodeURIComponent(section.id)}`)
          return
        }

        if (section.urlSlug) {
          setPendingBrowseRouteId(section.id)
          navigate(`/catalog?category=${encodeURIComponent(section.urlSlug)}`)
          return
        }

        void loadMoreBrowseRowItems(section.id)
      }}
      onEndReached={() => {
        if (section.canLoadMore) {
          void loadMoreBrowseRowItems(section.id)
        }
      }}
    />
  )

  const content = (() => {
    if (!catalogQuery.data && catalogQuery.isPending) {
      return (
        <div className="flex h-full min-h-[320px] items-center justify-center">
          <p className="text-[#FEFEFEB2]">Загружаем каталог...</p>
        </div>
      )
    }

    if (!catalogQuery.data && catalogQuery.isError) {
      return (
        <div className="flex h-full min-h-[320px] items-center justify-center">
          <p className="text-center text-[#FEFEFEB2]">{summarizeApiError(catalogQuery.error)}</p>
        </div>
      )
    }

    if (!catalogQuery.data) {
      return (
        <div className="flex h-full min-h-[320px] items-center justify-center">
          <p className="text-[#FEFEFEB2]">Каталог временно недоступен</p>
        </div>
      )
    }

    if (catalogQuery.data.mode === 'search') {
      const navigationTargetFavorites = pendingNavigationItem
        ? pendingNavigationItem === 'bookmarks'
        : favoritesOnly
      const isNavigationPending =
        pendingNavigationItem !== null &&
        (navigationTargetFavorites !== favoritesOnly || catalogQuery.isFetching)
      const shouldShowFavoritesBackButton = favoritesOnly && !isFavoritesOverviewMode
      const hasFavoritesOverviewContent = Boolean(
        isFavoritesOverviewMode && favoritesOverview && favoritesOverview.recentHistory.total > 0
      )

      if (searchItems.length === 0 && !hasFavoritesOverviewContent) {
        return (
          <section className="flex w-full flex-col gap-4">
            {!favoritesOnly ? (
              <CatalogBackButton
                onClick={() => {
                  handleExitSearchMode()
                }}
              />
            ) : shouldShowFavoritesBackButton ? (
              <CatalogBackButton
                onClick={() => {
                  handleFavoritesOverviewBack()
                }}
              />
            ) : null}
            {!isFavoritesOverviewMode && searchHeadingTitle ? (
              <h2 style={catalogSectionTitleStyle}>{searchHeadingTitle}</h2>
            ) : null}
            <div className="flex h-full min-h-[320px] items-center justify-center">
              <p className="text-[#FEFEFEB2]">
                {navigationTargetFavorites && isNavigationPending
                  ? 'Открываем избранное...'
                  : historyOnly
                    ? 'Вы ещё не смотрели материалы.'
                    : favoritesOnly
                      ? 'В избранном пока ничего нет.'
                      : 'Ничего не найдено.'}
              </p>
            </div>
          </section>
        )
      }

      return (
        <section className="flex w-full flex-col gap-4">
          {!favoritesOnly ? (
            <CatalogBackButton
              onClick={() => {
                handleExitSearchMode()
              }}
            />
          ) : shouldShowFavoritesBackButton ? (
            <CatalogBackButton
              onClick={() => {
                handleFavoritesOverviewBack()
              }}
            />
          ) : null}
          {!isFavoritesOverviewMode ? (
            <h2 style={catalogSectionTitleStyle}>{searchHeadingTitle}</h2>
          ) : null}
          {isFavoritesOverviewMode && favoritesOverview ? (
            <>
              <div className="flex w-full flex-col gap-4">
                <h2 style={catalogSectionTitleStyle}>Вы сохранили</h2>
                <div className={responsiveCatalogCardGridClass}>
                  {searchItems.length > 0 ? (
                    searchItems.map((item) => (
                      <VideoCard
                        key={itemId(item)}
                        className="!w-full max-w-none"
                        {...toVideoCardProps(
                          item,
                          navigate,
                          detailRouteSearch,
                          bookmarkPendingIds.has(itemId(item)),
                          () => {
                            void handleBookmarkToggle(item)
                          },
                          prefetchViewerForItem
                        )}
                      />
                    ))
                  ) : (
                    <p className="text-[#FEFEFEB2]">В избранном пока ничего нет.</p>
                  )}
                </div>
              </div>
              {favoritesOverview.recentHistory.items.length > 0 ? (
                <div className="flex w-full flex-col gap-4">
                  <div className="flex w-full items-center justify-between gap-4">
                    <h2 style={catalogSectionTitleStyle}>Вы смотрели ранее</h2>
                    {favoritesOverview.recentHistory.total >
                    favoritesOverview.recentHistory.items.length ? (
                      <button
                        type="button"
                        style={catalogSectionViewAllStyle}
                        onClick={() => {
                          const nextParams = buildFavoritesOverviewParams()
                          nextParams.set('history', '1')
                          setSearchParams(nextParams, { replace: true })
                        }}
                      >
                        Посмотреть все
                      </button>
                    ) : null}
                  </div>
                  <div className={responsiveCatalogCardGridClass}>
                    {favoritesOverview.recentHistory.items.map((item) => (
                      <VideoCard
                        key={itemId(item)}
                        className="!w-full max-w-none"
                        {...toVideoCardProps(
                          item,
                          navigate,
                          detailRouteSearch,
                          bookmarkPendingIds.has(itemId(item)),
                          () => {
                            void handleBookmarkToggle(item)
                          },
                          prefetchViewerForItem
                        )}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <div className={responsiveCatalogCardGridClass}>
              {searchItems.map((item) => (
                <VideoCard
                  key={itemId(item)}
                  className="!w-full max-w-none"
                  {...toVideoCardProps(
                    item,
                    navigate,
                    detailRouteSearch,
                    bookmarkPendingIds.has(itemId(item)),
                    () => {
                      void handleBookmarkToggle(item)
                    },
                    prefetchViewerForItem
                  )}
                />
              ))}
            </div>
          )}
          {searchNextCursor ? (
            <div className="flex items-center justify-center pt-2">
              <button
                type="button"
                className="cursor-pointer rounded-lg border border-[#404040] px-4 py-2 text-[#FEFEFE] transition-colors hover:bg-[#2A2A2A] disabled:cursor-default disabled:opacity-60"
                disabled={isSearchLoading}
                onClick={() => {
                  void loadMoreSearchResults()
                }}
              >
                {isSearchLoading ? 'Загрузка...' : 'Показать ещё'}
              </button>
            </div>
          ) : null}
        </section>
      )
    }

    if (browseSections.length === 0) {
      return (
        <div className="flex h-full min-h-[320px] items-center justify-center">
          <p className="text-[#FEFEFEB2]">Пока нет доступных материалов.</p>
        </div>
      )
    }

    if (viewerIsInactive) {
      const bundleSections = browseSections.filter((section) => section.rowKind === 'bundle')
      const categorySections = browseSections.filter((section) => section.rowKind !== 'bundle')
      const shouldShowUpsell = browseSections.length > 0

      return (
        <>
          {bundleSections.map(renderBrowseSection)}
          {shouldShowUpsell ? (
            <VideoSubscriptionUpsell
              onCtaClick={() => {
                navigate(routes.academyPricing)
              }}
            />
          ) : null}
          {categorySections.map(renderBrowseSection)}
        </>
      )
    }

    return <>{browseSections.map(renderBrowseSection)}</>
  })()

  const activeNavigationItem = pendingNavigationItem ?? (favoritesOnly ? 'bookmarks' : 'home')
  const isNavigationPending =
    pendingNavigationItem !== null &&
    ((pendingNavigationItem === 'bookmarks') !== favoritesOnly || catalogQuery.isFetching)
  const shortcuts = [
    ...(hasAdminAccess(user) ? [{ id: 'admin' as const, label: 'Admin', href: routes.admin }] : []),
    ...(hasSuperAdminAccess(user)
      ? [{ id: 'super-admin' as const, label: 'Super Admin', href: routes.superAdmin }]
      : []),
  ]

  return (
    <VideosShell
      activeNavigationItem={activeNavigationItem}
      onNavigationSelect={handleNavigationSelect}
      currentPathname={location.pathname}
      shortcuts={shortcuts}
      searchValue={searchInput}
      onSearchValueChange={setSearchInput}
      categories={categories}
      selectedCategorySlug={catalogQuery.data?.query.category ?? null}
      onCategorySelect={handleCategorySelect}
      bundles={bundles}
      selectedBundleId={catalogQuery.data?.query.bundle ?? null}
      onBundleSelect={handleBundleSelect}
      filterSections={filterSections}
      onFilterSelectionChange={handleFilterSelectionChange}
      onResetFilters={handleResetFilters}
      onLogout={async () => {
        await logout()
        navigate(routes.registration, { replace: true })
      }}
    >
      <main
        ref={mainScrollRef}
        onScroll={handleCatalogScroll}
        className="flex min-h-0 w-full flex-1 overflow-x-hidden overflow-y-auto px-6 pb-8 pt-8"
      >
        <div className="flex w-full flex-col gap-10">
          {isNavigationPending ? (
            <p className="text-[#FEFEFEB2]" role="status" aria-live="polite">
              {pendingNavigationItem === 'bookmarks'
                ? 'Открываем избранное...'
                : 'Возвращаемся в каталог...'}
            </p>
          ) : null}
          {categoryItemsError ? (
            <p className="text-[#FEFEFEB2]" role="status">
              {categoryItemsError}
            </p>
          ) : null}
          {searchItemsError ? (
            <p className="text-[#FEFEFEB2]" role="status">
              {searchItemsError}
            </p>
          ) : null}
          {content}
          {catalogQuery.data?.mode === 'browse' && isBrowseCategoriesLoading ? (
            <p className="text-center text-[#FEFEFEB2]">
              {browseSource === 'bundles' ? 'Загружаем подборки...' : 'Загружаем категории...'}
            </p>
          ) : null}
          {catalogQuery.data ? <div ref={loadMoreSentinelRef} className="h-1 w-full" /> : null}
        </div>
      </main>
    </VideosShell>
  )
}

VideosPage.displayName = 'VideosPage'
